import {AfterViewInit, Component, ElementRef, EventEmitter, OnInit, Output, ViewChild} from '@angular/core';
import {NgClass, NgForOf, NgIf, NgStyle} from "@angular/common";
import {RouterLink} from "@angular/router";
import {ApplicationProcessModule} from '../application-process/application-process.module';
import {CarouselComponent} from './carousel/carousel.component';
import {TabsComponent} from './tabs/tabs.component';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {ModalService} from '../../../shared/services/modal.service';
import {FormComponent} from '../form/form.component';

@Component({
    selector: 'app-home',
    standalone: true,
    imports: [
        NgIf,
        NgForOf,
        RouterLink,
        NgClass,
        RouterLink,
        NgForOf,
        NgStyle,
        ApplicationProcessModule,
        CarouselComponent,
        TabsComponent,
        FormsModule,
        ReactiveFormsModule,
    ],
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss']
})
export class HomeComponent {
    @ViewChild(FormComponent) formComponent!: FormComponent;

    isModalVisible = false;
    isModalOpen = false;
    @Output() formSubmitted = new EventEmitter<boolean>();  // Event emitter to notify parent

    isPlaying = false;
    learning = [
        {
            icon: 'fa-solid fa-mobile-screen',
            title: 'Advanced Web-Mobile Platforms',
            'content': ' Volmint’s systems support multi-device facilities and can be used with advanced web mobile platforms. It offers a seamless user experience and enhances flexibility in administration.',
        },
        {
            'icon': 'fa-solid fa-universal-access',
            'title': 'Role-based Login Access',
            'content': 'Volmint CMS prioritizes data security through role-based login access, ensuring specific permission to users based on their roles and responsibilities, ensuring integrity and confidentiality. \n' +
                '\n',
        },
        {
            'icon': 'fa-solid fa-magnifying-glass-chart',
            'title': 'Monitoring & Accurate Information Analysis\n',
            'content': 'The system enables real-time monitoring and precise analysis of data, which offers administrators valuable insights for informed decision-making. The feature enhances operational efficiency and strategic planning within the college.\n' +
                '\n',
        },
        {
            'icon': 'fa-regular fa-folder-open',
            'title': 'Document Management and Generation\n',
            'content': 'Volmint CMS facilitates comprehensive document management. Thus, ensuring organized storage and efficient retrieval of essential records. It also simplifies the generation of various documents, streamlining administrative tasks and reducing paperwork.',
        },
        {
            'icon': 'fa-solid fa-bell',
            'title': 'On-time Notifications\n',
            'content': 'The platform ensures timely communication by sending notifications through multiple channels such as SMS, WhatsApp, and email. The feature enhances communication efficiency, keeping stakeholders informed promptly about important updates and events.\n' +
                '\n',
        },
        {
            'icon': 'fa-solid fa-cloud-moon',
            'title': '24*7 Onsite & Online Service Support\n',
            'content': 'Volmint CMS provides round-the-clock onsite and online service support, ensuring that technical issues are addressed promptly. This commitment to continuous support enhances the reliability and functionality of the system for uninterrupted operations.\n' +
                '\n',
        },

    ]
    offerings = [
        {
            text: 'Lifetime Free Maintenance',
            icon : 'fas fa-tools',
            color   : 'text-red-500'
        },
        {
            text: 'Personalized Assistance',
            icon : 'fas fa-headset',
            color: 'text-cyan-500'
        },

        {
            text: 'Discounts on Extra Modules',
            icon : 'fas fa-percent',
            color: 'text-blue-500'

        },
        {
            text: 'Feedback and Feature Request',
            icon : 'fas fa-comment-dots',
            color: 'text-green-500'

        },
        {
            text: 'Third-Party Tool Integration',
            icon : 'fas fa-puzzle-piece',
            color: 'text-purple-500'

        },
        {
            text: 'Consultation and Best Practices',
            icon : 'fas fa-user-tie',
            color: 'text-red-800'

        },
        {
            text: 'Training Sessions',
            icon : 'fas fa-clipboard-list',
            color : 'text-lime-500'

        },
        {
            text: 'Resolution within 48 hours',
            icon : 'fas fa-hourglass',
            color: 'text-pink-500'

        },
        {
            text: 'Free Server',
            icon : 'fas fa-server',
            color   : 'text-orange-500'
        },
    ];

    features = [
        {
            icon: 'fa-regular fa-address-card',
            iconColor : 'text-red-500',
            title : ' Admissions Management',
            pointers : ['Student Counseling', 'Online Registration', 'Document Verification', 'Student Verification', 'Payment Process', 'Course', 'Program Selection', 'Bulk Upload Students', 'Auto-Generated ID Card', 'Secure Database']
        },
        {
            icon: 'fa-solid fa-user-graduate',
            iconColor: 'text-green-500',
            title : ' Student Management',
            pointers: [
                'Student Information',
                'Student Verifications',
                'Student Transactions',
                'Student Attendance',
                'Student Enrollment',
                'Edit/Update Student Details',
                'Progress Manager',
                'Comprehensive Reports',
                'Student Documents'
            ]

        },
        {
            icon: 'fas fa-user',
            iconColor: 'text-orange-500',
            title : 'Employee Management',
            pointers: [
                'Assign and Create Departments',
                'Add and Assign Designations',
                'Add and Edit Employee Details',
                'Employee Performance Management',
                'Role Based Permissions',
                'Reports and Analytics'
            ]

        },
        {
            icon: 'fas fa-money-bill-wave',
            iconColor: 'text-pink-500',
            title : ' Fee and Payroll Management',
            pointers: [
                    'Real-Time Fee Tracking',
                    'Payment Gateways',
                    'Live Fee Collection Updates',
                    'Auto-Generated Fee Slips',
                    'Automated Notifications',
                    'Faculty Payroll Tracking',
                    'Auto-Generated Salary Slips',
                    'E-receipts and Transaction Details'
            ]
        },
        {
            icon: 'fas fa-chalkboard-teacher',
            iconColor: 'text-yellow-400',
            title : ' Learning Management System',
            pointers: [
                'Create, update, and organize courses',
                'Assign courses',
                'Upload SLM, notes, and PDFs',
                'Provide access to e-books',
                'Monitor student participation',
                'Generate real-time reports'
            ]

        },
        {
            icon: 'fas fa-file-archive',
            title : ' Document Management',
            iconColor: 'text-cyan-500',
            pointers: [
                'Store Student Academic Records',
                'Issue ID Card, Certificates, and Marksheets',
                'Maintain digital transcripts',
                'Fee receipts and financial documents',
                "Student's Original Degree",
                'Maintain Student’s Queries'
            ]

        },
        {
            icon: 'fas fa-file-alt',
            iconColor: 'text-blue-500',
            title : 'Exam Management',
            pointers: [
                'Admit Card Issue',
                'Add Exam Sessions',
                'Upload Questions',
                'Schedule Exam Date and Sessions',
                'Check Student Eligibility',
                'Sitting Arrangement/ Add Halls',
                'Result Announcement'
            ]

        },
        {
            icon: 'fas fa-chart-line',
            iconColor: 'text-pink-800',
            title : ' Accounts Management',
            pointers: [
                'Add Money',
                'Track Transactions',
                'Payment Gateways',
                'Auto-Generated Receipts',
                'View Detailed Reports',
                'Live Payment Updated',
                'University Account Statements',
            ]
        },
        {
            icon: 'fas fa-building',
            iconColor: 'text-red-500',
            title : 'Hostel Management',
            pointers: [
                'Add & Manage Hostels',
                'Assign Hostel Rooms',
                'Monitor Student Details',
                'Track Visitor\'s Check-Ins',
                'Add Menu by Days',
                'Track Hostels Payments',
            ]
        },
        {
            icon: 'fas fa-bus',
            iconColor: 'text-pink-600',
            title : 'Vehicle Management',
            pointers: [
                'Add & Manage Vehicles',
                'Assign Driver',
                'Add Student To The Vehicle',
                'Manage bus timings & stops',
                'Track and Optimize Transportation Routes',
                'Document Verification & Management',
                'Track Account Details',
            ]
        },
        {
            icon: 'fas fa-book',
            iconColor: 'text-green-300',
            title : ' Library Management',
            pointers: [
                'Issue Books',
                'Organize, Search, and Track Books.',
                'Book Borrowings',
                'Tracking of lost & Damage Issued Books',
                'Calculate Book Charges',
                'Manage E-books & Journals',
                'Book Transactions & Payment Tracking',
            ]
        },
        {
            icon: 'fas fa-phone-volume',
            iconColor: 'text-orange-900',
            title : 'Support & Communication',
            pointers: [
                'Add and Manage Notifications',
                'Multi-Channel Communication',
                'Real-Time Announcements & Alerts',
                'Feedback & Grievance Management',
                'Easy Access to Various Tools',
            ]
        },
    ]

    constructor(private modalService: ModalService) {}

    playVideo() {
        this.isPlaying = true;
    }

    openModal() {
        this.modalService.openModal()
    }
    submit() {
        if (this.formComponent) {
            this.formComponent.onSubmit();
        }
    }
}
